import { Box, Checkbox, FormControlLabel } from "@material-ui/core"
import AddIcon from "@material-ui/icons/Add"
import CreateIcon from "@material-ui/icons/Create"
import DeleteIcon from "@material-ui/icons/Delete"
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox"
import VisibilityIcon from "@material-ui/icons/Visibility"
import { useMemo, useState } from "react"
import type { MouseEvent, ReactElement } from "react"
import Share from "@material-ui/icons/Share"
import { useTranslation } from "react-i18next"

import type { IConfirmationDialogProps } from ".."
import { CardTemplate, ConfirmationDialog, ContextMenuButton, IconMenuItem } from ".."
import type { ProjectComparisonDto } from "../../../models"
import { isReverse, isVertical } from "../../../utils/comparison-layout.utils"
import { LastEditDateCaption, SubTitle1 } from "../../atoms"
import { useAuthenticatedUser } from "../../indentity-provider.component"
import { CardThumbnail } from "../../atoms/card-thumbnail/card-thumbnail.component"

import "./comparison-card.styles.scss"

export interface IComparisonCardProps {
  comparison: ProjectComparisonDto
  selectable?: boolean | undefined
  selected?: boolean | undefined
  disableEditing?: boolean | undefined
  showInFolder?: boolean | undefined
  onOpenComparison?: (comparison: ProjectComparisonDto) => void
  onChangeBundle?: (comparison: ProjectComparisonDto) => void
  onRemove?: (comparison: ProjectComparisonDto) => void
  onRequestUpdate?: (comparison: ProjectComparisonDto) => void
  onShareUrlClick?: (comparisonId: string) => void
}

export function ComparisonCard(props: IComparisonCardProps): ReactElement {
  const {
    comparison,
    selectable,
    selected,
    disableEditing,
    showInFolder,
    onOpenComparison,
    onChangeBundle,
    onRemove,
    onRequestUpdate,
    onShareUrlClick
  } = props

  const { t } = useTranslation(["myBosatlas", "general"])

  const { isTeacher } = useAuthenticatedUser()

  const [confirmDialogProps, setConfirmDialogProps] = useState<IConfirmationDialogProps>({
    open: false,
    confirmText: t("general:dialog.delete"),
    title: "",
    bodyText: "",
    onConfirm: () => {
      if (onRemove) onRemove(comparison)
    },
    onCancel: closeConfirmDialog
  })

  const shouldAllowActions: boolean = useMemo<boolean>(() => {
    if (isTeacher) return true
    if (comparison.sharedClass) return false
    return true
  }, [isTeacher, comparison])

  const projectImages: string[] = useMemo<string[]>(() => {
    const imageUrls: string[] = [
      comparison.firstAssetThumbnailUrl as string,
      comparison.secondAssetThumbnailUrl as string
    ]

    if (comparison.thirdAssetThumbnailUrl) {
      imageUrls.push(comparison.thirdAssetThumbnailUrl)
    }
    if (comparison.fourthAssetThumbnailUrl) {
      imageUrls.push(comparison.fourthAssetThumbnailUrl)
    }

    return imageUrls
  }, [comparison])

  function handleURLShareClick(): void {
    if (onShareUrlClick) onShareUrlClick(comparison.id)
  }

  function closeConfirmDialog(): void {
    setConfirmDialogProps({ ...confirmDialogProps, open: false })
  }

  function getGridClassName(): string {
    switch (projectImages.length) {
      case 2:
        return `grid-2 ${isVertical(comparison.layout) ? "vertical" : "horizontal"}`
      case 3:
        return `grid-3 ${
          isVertical(comparison.layout) ? "vertical" : "horizontal"
        } ${isReverse(comparison.layout) ? "reverse" : "no-reverse"}`
      case 4:
        return "grid-4"
      default:
        return ""
    }
  }

  function handleOpenComparison(event?: MouseEvent): void {
    if (event) event.stopPropagation()
    if (onOpenComparison) onOpenComparison(comparison)
  }

  function handleChangeBundle(): void {
    if (onChangeBundle) onChangeBundle(comparison)
  }

  function handleRemove(): void {
    const title: string = comparison.sharedClass
      ? t("myBosatlas:remove.removeSharedComparison")
      : t("myBosatlas:remove.removeComparison")
    const bodyText: string = comparison.sharedClass
      ? t("myBosatlas:remove.removeSharedComparisonBodyText")
      : t("myBosatlas:remove.removeComparisonBodyText")
    setConfirmDialogProps({
      ...confirmDialogProps,
      open: true,
      title,
      bodyText
    })
  }

  function handleRequestNameChange(): void {
    if (onRequestUpdate) onRequestUpdate(comparison)
  }

  function getChangeBundleTitle(): string {
    return showInFolder ? t("myBosatlas:cards.moveTo") : t("myBosatlas:cards.addFolder")
  }

  function ChangeBundleIcon(): ReactElement {
    return showInFolder ? <MoveToInboxIcon color="primary" /> : <AddIcon color="primary" />
  }

  function getRemoveTitle(): string {
    return showInFolder ? t("myBosatlas:cards.deleteFolder") : t("myBosatlas:cards.delete")
  }

  function getSharedComparisonCaptionString(): string | undefined {
    if (!comparison.sharedClass || !comparison.lastModified) return undefined

    return isTeacher
      ? t("myBosatlas:cards.lastSharedOnDate", {
          date: comparison.lastModified?.toString(),
          class: comparison.sharedClass
        })
      : t("myBosatlas:cards.sharedOnDate", {
          date: comparison.lastModified?.toString()
        })
  }

  function Header(): ReactElement {
    return (
      <FormControlLabel
        control={<Checkbox color="primary" checked={selected} onClick={handleOpenComparison} />}
        label={t("myBosatlas:cards.add")}
        className="selectable-checkbox"
      />
    )
  }

  function Thumbnail(): ReactElement {
    return (
      <Box className="comparison-card-header">
        <Box className={`comparison-card-header-images ${getGridClassName()}`}>
          {projectImages.map((image: string, index: number) => (
            <CardThumbnail key={`${index}-${image}`} image={image} />
          ))}
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <CardTemplate
        header={selectable ? <Header /> : undefined}
        onClick={handleOpenComparison}
        image={<Thumbnail />}
        className="comparison-card"
        selected={selected}
        big
      >
        <Box className="comparison-card-content">
          <Box>
            <SubTitle1>
              <b>{comparison.title}</b>
            </SubTitle1>
            <LastEditDateCaption date={comparison.lastModified} customString={getSharedComparisonCaptionString()} />
          </Box>
          {!disableEditing && !selectable && (
            <ContextMenuButton className="comparison-context" closeOnSelect>
              <IconMenuItem
                icon={<VisibilityIcon color="primary" />}
                label={t("myBosatlas:cards.showComparison")}
                onClick={handleOpenComparison}
              />
              {shouldAllowActions && [
                <IconMenuItem
                  key="share-menuitem"
                  icon={<Share color="primary" />}
                  label={t("myBosatlas:cards.shareURLComparison")}
                  onClick={handleURLShareClick}
                />,
                <IconMenuItem
                  key="create-menuitem"
                  icon={<CreateIcon color="primary" />}
                  label={t("myBosatlas:cards.changeName")}
                  onClick={handleRequestNameChange}
                />,
                <IconMenuItem
                  key="change-menuitem"
                  icon={<ChangeBundleIcon />}
                  label={getChangeBundleTitle()}
                  onClick={handleChangeBundle}
                />,
                <IconMenuItem
                  key="delete-menuitem"
                  icon={<DeleteIcon color="primary" />}
                  label={getRemoveTitle()}
                  onClick={handleRemove}
                />
              ]}
            </ContextMenuButton>
          )}
        </Box>
      </CardTemplate>
      <ConfirmationDialog {...confirmDialogProps} />
    </Box>
  )
}
